import { useMutation } from "@tanstack/react-query";
import { AlertTriangle, CalendarX, CheckCircle2, CreditCard, MapPinOff, ScanLine, XCircle } from "lucide-react";
import type { JSX } from "react";
import { useState } from "react";

import { apiRequest } from "../../common/api/apiClient.js";
import type { components } from "../../generated/api.js";

type ScanResultDTO = components["schemas"]["ScanResultDTO"];

/**
 * Jedes Ergebnis trägt vier Träger: Farbe, Symbol, Überschrift und Statuscode. Im
 * Dunkeln, mit Handschuhen, in Eile muss die Antwort in unter einer Sekunde ankommen
 * (StyleGuide, Scan-Ergebnis).
 *
 * Die drei Ergebnisfarben sind eigene, dunklere Werte als die Tokens: das Vollbild
 * trägt weißen Text, und dafür braucht der Grund mehr Tiefe.
 */
const RESULTS: Record<
  ScanResultDTO["result"],
  { title: string; background: string; Icon: typeof CheckCircle2 }
> = {
  VALID: { title: "Gültig", background: "#0B6E4F", Icon: CheckCircle2 },
  ALREADY_USED: { title: "Schon eingelöst", background: "#8A5A00", Icon: AlertTriangle },
  NOT_PAID: { title: "Nicht bezahlt", background: "#8A5A00", Icon: CreditCard },
  CANCELLED: { title: "Storniert", background: "#9B1E24", Icon: XCircle },
  INVALID: { title: "Ungültig", background: "#9B1E24", Icon: XCircle },
  // Echt, aber für einen anderen Einlass: Bernstein wie „schon eingelöst" — kein
  // Betrug, nur der falsche Ort. Darunter steht, wohin der Gast gehört.
  NOT_HERE: { title: "Nicht für diesen Einlass", background: "#8A5A00", Icon: MapPinOff },
  // Mitgliedsausweis ausserhalb seiner Laufzeit.
  EXPIRED: { title: "Abgelaufen", background: "#9B1E24", Icon: CalendarX },
};

/**
 * Die Kontrolleur-App hat eine einzige Hauptaktion. Die Kamera kommt als nächster
 * Schritt dazu; bis dahin wird der Code eingetippt oder von einem Handscanner
 * hereingegeben — derselbe Endpunkt, dieselbe Antwort.
 */
export function ScanPage(): JSX.Element {
  const [code, setCode] = useState("");

  const scan = useMutation({
    mutationFn: (value: string) =>
      apiRequest<ScanResultDTO>("/bookings/scan", { method: "POST", body: { code: value } }),
  });

  const result = scan.data ? RESULTS[scan.data.result] : null;

  return (
    <div className="mx-auto w-full max-w-md px-4 py-6">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (code.trim().length > 0) {
            scan.mutate(code.trim());
          }
        }}
      >
        <label className="micro block" htmlFor="scan-code">
          Buchungscode
        </label>
        <input
          id="scan-code"
          className="input num mt-1.5"
          value={code}
          onChange={(event) => setCode(event.target.value)}
          placeholder="ABCDE-FGHJK-MNPQR-STUVW"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
        />
        <button type="submit" className="btn wide lg mt-3" disabled={scan.isPending}>
          <ScanLine size={19} aria-hidden="true" />
          {scan.isPending ? "Wird geprüft …" : "Prüfen"}
        </button>
      </form>

      {scan.isError && (
        <p className="mt-4 text-[14px] text-danger">
          Der Scan ist fehlgeschlagen. Bitte noch einmal versuchen.
        </p>
      )}

      {scan.data && result && (
        <output
          className="mt-5 grid place-items-center rounded-lg px-5 py-9 text-center text-white"
          style={{ background: result.background }}
        >
          <result.Icon size={56} aria-hidden="true" />
          <p className="mt-3 text-[26px] font-extrabold tracking-[-0.03em]">{result.title}</p>
          <p className="num mt-1 text-[12.5px] font-bold uppercase tracking-[0.06em] opacity-85">
            {scan.data.result} · {scan.data.statusCode}
          </p>
          {scan.data.access && (
            <p className="mt-3 text-[17px] font-extrabold">
              {scan.data.access === "CHECK_IN" ? "Eingecheckt" : "Ausgecheckt"}
            </p>
          )}
          {/* Was der Einlass braucht, um den Gast weiterzuschicken — und den Namen,
              weil das Ticket auf ihn ausgestellt ist und gegen einen Ausweis geprüft
              wird. Mehr kommt vom Server nicht mit. */}
          {scan.data.holderName && (
            <p className="mt-4 text-[19px] font-extrabold tracking-[-0.02em]">
              {scan.data.holderName}
            </p>
          )}
          {scan.data.serviceTitle && (
            <p className="mt-1 text-[14px] font-semibold opacity-95">{scan.data.serviceTitle}</p>
          )}
          <p className="num mt-1 text-[13px] opacity-85">
            {[
              scan.data.startsAt
                ? new Date(scan.data.startsAt).toLocaleString("de-DE", {
                    weekday: "short",
                    day: "2-digit",
                    month: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : null,
              scan.data.venueName,
              scan.data.route,
            ]
              .filter(Boolean)
              .join("  ·  ")}
          </p>
          {scan.data.seats.length > 0 ? (
            <p className="num mt-2 text-[17px] font-extrabold">
              {scan.data.seats.join(" · ")}
            </p>
          ) : (
            scan.data.quantity !== null && (
              <p className="num mt-2 text-[15px] font-bold">
                {scan.data.quantity}× · freie Platzwahl
              </p>
            )
          )}
          {scan.data.result === "ALREADY_USED" && scan.data.usedAt && (
            <p className="num mt-3 text-[12px] opacity-85">
              Eingelöst: {new Date(scan.data.usedAt).toLocaleString("de-DE")}
            </p>
          )}
          <button
            type="button"
            className="mt-6 min-h-[44px] rounded-full bg-white/15 px-5 text-[14px] font-bold text-white"
            onClick={() => {
              scan.reset();
              setCode("");
            }}
          >
            Nächster Scan
          </button>
        </output>
      )}
    </div>
  );
}
